import { z } from 'zod';
import type { AggregateOp } from './field-types';
import { dateGranularitySchema } from './record-group';
import { type ViewFilterNode, viewFilterNodeSchema } from './view';

/**
 * WHAT A REPORT IS (`docs/decisions/reports.md` R2, R3, T5).
 *
 * One saved question about the records the CRM already holds: which object, which number,
 * split how, over what stretch of time, drawn as what. It is WORKSPACE CONTENT rather
 * than a preference (T5) - one team, one set of reports, the same shape `saved_view`
 * takes - and it is stored as a single validated jsonb document rather than as a dozen
 * columns, because every one of those columns would be nullable for some chart type and
 * a nullable column is a shape nobody can read off the schema.
 *
 * VALIDATED ON WRITE AND ON READ, which is the whole reason the schema lives in
 * `packages/shared` rather than in the server (INV-11). A definition written by an older
 * build, or by hand in psql, must not reach a chart as a shape the chart does not expect;
 * and the browser needs the same schema to build the editor's form without a second
 * description of the same thing drifting away from this one.
 *
 * IT NAMES UUIDs, NOT NAMES (INV-7). `objectId`, `fieldId` and the filter's field ids are
 * metadata UUIDs, so renaming an object or a field cannot break a report. That makes a
 * definition instance-local, exactly as `saved_view.column_field_ids` is; the ROW still
 * carries `universal_id` and `origin`, which is what makes the eight seeded reports
 * re-runnable and diffable across instances.
 */

/**
 * THE METRIC: what number a group is worth.
 *
 * `count` is the only op that needs no field - it counts records - and every other op
 * needs one, which is the same split `AggregateRequest` makes on the wire. The op set is
 * narrowed to what a CHART can plot: `countValues`, `countEmpty`, `countUnique`,
 * `countTrue` and `countFalse` are real aggregates and are honest answers in a table
 * footer, but "how many of these are empty, per month" is a data-quality question rather
 * than a business one, and R2 lists exactly these five.
 */
export const REPORT_METRIC_OPS = ['count', 'sum', 'avg', 'min', 'max'] as const;
export type ReportMetricOp = (typeof REPORT_METRIC_OPS)[number];

export const reportMetricSchema = z
  .strictObject({
    op: z.literal([...REPORT_METRIC_OPS]),
    /** Required for every op except `count`, and refused for `count`. */
    fieldId: z.uuid().optional(),
  })
  .refine((metric) => (metric.op === 'count') === (metric.fieldId === undefined), {
    message: 'count takes no field; sum, avg, min and max each need one',
  });

export type ReportMetric = z.infer<typeof reportMetricSchema>;

/** The metric as the grouped read wants it. `count` drops the field, the rest carry it. */
export function metricAggregate(metric: ReportMetric): { op: AggregateOp; fieldId?: string } {
  return metric.fieldId === undefined
    ? { op: metric.op }
    : { op: metric.op, fieldId: metric.fieldId };
}

/**
 * THE DIMENSION: what the x axis (or the ring, or the funnel) is made of.
 *
 * `granularity` is what turns a DATE column into months rather than into one group per
 * distinct day, and it is refused on anything that is not a date - that check needs the
 * field's TYPE and therefore belongs to the server's validation rather than to zod.
 */
export const reportDimensionSchema = z.strictObject({
  fieldId: z.uuid(),
  granularity: dateGranularitySchema.optional(),
});

export type ReportDimension = z.infer<typeof reportDimensionSchema>;

/**
 * THE CHART TYPES, closed at six (R3), and each one is a claim about the data.
 *
 * `bar` and `line` are the only two that take a SEGMENT, because they are the only two
 * with an axis to stack or to lay a second line along. A donut divides one whole and a
 * funnel narrows one population; both are single-dimension by construction, and a
 * "segmented donut" is two rings that share no baseline. `number` is the existing
 * `StatTile` with no dimension at all.
 */
export const REPORT_CHART_TYPES = [
  'bar',
  'bar_stacked',
  'line',
  'area',
  'donut',
  'funnel',
  'number',
] as const;
export type ReportChartType = (typeof REPORT_CHART_TYPES)[number];

/** The two that can carry a second split (R2: "only for bar and line"). */
export function chartAcceptsSegment(chart: ReportChartType): boolean {
  return chart === 'bar' || chart === 'bar_stacked' || chart === 'line' || chart === 'area';
}

/** A big number has no dimension to group by; every other chart must have one. */
export function chartNeedsDimension(chart: ReportChartType): boolean {
  return chart !== 'number';
}

/**
 * THE TIME RANGES, closed at five (R3). No custom from/to in this round, which is R3's
 * own sentence and not an omission.
 *
 * `all_time` is not "no filter" by accident - it is the explicit answer to "over what
 * stretch", so a report that means it says so rather than leaving the field out.
 */
export const REPORT_RANGES = [
  'last_30_days',
  'this_quarter',
  'last_12_months',
  'this_year',
  'all_time',
] as const;
export type ReportRange = (typeof REPORT_RANGES)[number];
export const reportRangeSchema = z.literal([...REPORT_RANGES]);

/**
 * THE DEFINITION.
 *
 * `rangeFieldId` is the one field whose absence needs explaining. A range is a window on
 * a DATE, and a report grouped by month already names that date - so leaving it out means
 * "the field I am grouping by". A report grouped by something that is not a date (a
 * lifecycle donut, a funnel over stages) has no such field, so it must name one, or say
 * `all_time` and mean it. That cross-field rule is checked below rather than left to the
 * caller, because the alternative is a report that silently ignores its own range.
 */
export const reportDefinitionSchema = z
  .strictObject({
    objectId: z.uuid(),
    metric: reportMetricSchema,
    /** Absent only for the `number` chart, which plots one aggregate and no groups. */
    groupBy: reportDimensionSchema.optional(),
    /** One level, bar and line only (R2). */
    segmentBy: reportDimensionSchema.optional(),
    chart: z.literal([...REPORT_CHART_TYPES]),
    range: reportRangeSchema,
    /** The DATE field the range windows. Defaults to `groupBy` when that is a date. */
    rangeFieldId: z.uuid().optional(),
    /** The existing filter AST, unchanged and compiled by the one compiler (INV-6). */
    filter: viewFilterNodeSchema.nullish(),
  })
  .superRefine((definition, ctx) => {
    if (chartNeedsDimension(definition.chart) && definition.groupBy === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['groupBy'],
        message: `a ${definition.chart} chart needs something to group by`,
      });
    }
    if (!chartNeedsDimension(definition.chart) && definition.groupBy !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['groupBy'],
        message: 'a big number plots one aggregate and has no groups',
      });
    }
    if (definition.segmentBy !== undefined && !chartAcceptsSegment(definition.chart)) {
      ctx.addIssue({
        code: 'custom',
        path: ['segmentBy'],
        message: `a ${definition.chart} chart cannot be segmented; only bar and line can`,
      });
    }
    if (
      definition.segmentBy !== undefined &&
      definition.groupBy !== undefined &&
      definition.segmentBy.fieldId === definition.groupBy.fieldId
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['segmentBy'],
        message: 'a report cannot be split by the field it is already grouped by',
      });
    }
    /**
     * A RANGE WITH NOTHING TO APPLY IT TO IS A LIE ON THE SCREEN. The report page prints
     * "Last 12 months" beside a number computed over all time, and nothing anywhere says
     * so. Refused at the schema rather than defaulted to `all_time`, because the two
     * mistakes a caller can make here - forgetting the field, or meaning all time - want
     * different answers and only one of them is silent.
     */
    if (
      definition.range !== 'all_time' &&
      definition.rangeFieldId === undefined &&
      definition.groupBy?.granularity === undefined
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['rangeFieldId'],
        message:
          'a range needs a date field: either group by a date bucket, or name rangeFieldId, or use all_time',
      });
    }
  });

export type ReportDefinition = z.infer<typeof reportDefinitionSchema>;

/** Which field the range windows, once the default above is resolved. */
export function rangeFieldOf(definition: ReportDefinition): string | null {
  if (definition.range === 'all_time') return null;
  if (definition.rangeFieldId !== undefined) return definition.rangeFieldId;
  return definition.groupBy?.granularity === undefined ? null : definition.groupBy.fieldId;
}

/** The stored report, as it travels on the wire. */
export interface Report {
  readonly id: string;
  readonly universalId: string;
  readonly origin: string;
  readonly name: string;
  readonly description: string | null;
  readonly definition: ReportDefinition;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export const REPORT_NAME_MAX = 120;
export const REPORT_DESCRIPTION_MAX = 500;

/** What a caller may send to create a report. The row's identity is the server's. */
export const reportWriteSchema = z.strictObject({
  name: z.string().trim().min(1).max(REPORT_NAME_MAX),
  description: z.string().trim().max(REPORT_DESCRIPTION_MAX).nullish(),
  definition: reportDefinitionSchema,
});

export type ReportWriteInput = z.infer<typeof reportWriteSchema>;

/** An edit. Every field optional, but `definition` is replaced whole when it is sent. */
export const reportPatchSchema = z
  .strictObject({
    name: z.string().trim().min(1).max(REPORT_NAME_MAX).optional(),
    description: z.string().trim().max(REPORT_DESCRIPTION_MAX).nullish(),
    definition: reportDefinitionSchema.optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'nothing to change' });

export type ReportPatchInput = z.infer<typeof reportPatchSchema>;

export function parseReportDefinition(input: unknown): ReportDefinition {
  return reportDefinitionSchema.parse(input);
}

/** The filter a definition carries, or null. Narrowing helper so callers stop repeating it. */
export function reportFilterOf(definition: ReportDefinition): ViewFilterNode | null {
  return definition.filter ?? null;
}
